import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './ShoppingList.css'
import { subscribeToShoppingList, updateShoppingListItems } from './utils/shoppingListStore'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const canShare = typeof navigator !== 'undefined' && !!navigator.share

const randomItemId = () =>
  window.crypto.randomUUID ? window.crypto.randomUUID() : String(Math.random())

const ShoppingListView = () => {
  useDocumentMeta({
    title: 'Lista de Compras | jarder.dev',
    description: 'Marque os itens da lista de compras compartilhada em tempo real.',
  })

  const { id } = useParams()
  const [items, setItems] = useState(null)
  const [status, setStatus] = useState('loading')
  const [itemInput, setItemInput] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const unsubscribe = subscribeToShoppingList(id, (loadedItems) => {
      setItems(loadedItems)
      setStatus(loadedItems ? 'ready' : 'notfound')
    })
    return unsubscribe
  }, [id])

  const pendingCount = useMemo(
    () => (items ? items.filter((item) => !item.done).length : 0),
    [items],
  )

  const persist = (nextItems) => {
    setItems(nextItems)
    updateShoppingListItems(id, nextItems)
  }

  const toggleItem = (itemId) => {
    persist(items.map((item) => (item.id === itemId ? { ...item, done: !item.done } : item)))
  }

  const removeItem = (itemId) => {
    persist(items.filter((item) => item.id !== itemId))
  }

  const addItem = () => {
    const trimmed = itemInput.trim()
    if (!trimmed) return
    persist([...items, { id: randomItemId(), text: trimmed, done: false }])
    setItemInput('')
    trackEvent('tool_generate', { tool: 'shopping_list_add_item' })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addItem()
    }
  }

  const shareUrl = window.location.href

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    trackEvent('tool_copy', { tool: 'shopping_list' })
  }

  const share = async () => {
    try {
      await navigator.share({ title: 'Lista de Compras', url: shareUrl })
      trackEvent('tool_generate', { tool: 'shopping_list_share' })
    } catch {
      // User cancelled the share sheet — not an error worth surfacing.
    }
  }

  if (status === 'loading') {
    return (
      <div className="tool-page">
        <p>Carregando lista...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Lista não encontrada</h1>
        <p className="tool-disclaimer">
          Esse link não existe (ou a lista foi removida).
        </p>
        <Link to="/ferramentas/lista-de-compras" className="tool-button">
          Criar uma nova lista
        </Link>
      </div>
    )
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Lista de Compras</h1>
      <p className="tool-disclaimer">
        {pendingCount === 0
          ? 'Tudo marcado, nada faltando!'
          : `${pendingCount} ${pendingCount === 1 ? 'item pendente' : 'itens pendentes'}.`}{' '}
        Qualquer pessoa com este link vê as mudanças em tempo real.
      </p>

      <ul className="shopping-list-items">
        {items.map((item) => (
          <li key={item.id} className={item.done ? 'shopping-list-item done' : 'shopping-list-item'}>
            <label>
              <input type="checkbox" checked={item.done} onChange={() => toggleItem(item.id)} />
              <span>{item.text}</span>
            </label>
            <button
              type="button"
              className="chip-remove"
              onClick={() => removeItem(item.id)}
              aria-label={`Remover ${item.text}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <div className="chip-input-row">
        <input
          type="text"
          value={itemInput}
          onChange={(e) => setItemInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Adicionar item, aperte Enter"
        />
        <button type="button" className="tool-button tool-button-secondary" onClick={addItem}>
          Adicionar
        </button>
      </div>

      <div className="tool-result-actions" style={{ marginTop: 24 }}>
        <button type="button" className="tool-button tool-button-secondary" onClick={copyLink}>
          {copied ? 'Link copiado!' : 'Copiar link da lista'}
        </button>
        {canShare && (
          <button type="button" className="tool-button tool-button-secondary" onClick={share}>
            Compartilhar
          </button>
        )}
      </div>
    </div>
  )
}

export default ShoppingListView
